import gql from 'graphql-tag'
import {
  basicResponseHandler,
  fetchBase,
  graphQLBase,
  rejectResponseAbove,
  extractJSON,
  throwOnDataErrors,
} from '..'
import FilesAPI from '../files'

export const organizationPermissionsAttrsFragment = `
  fragment organizationPermissionsAttrs on OrganizationPermissions {
    read
    write
    manage
    manageUsers
    manageChannels
    manageBilling
    manageIntegrations
    invite
    createChannel
  }
`

export const organizationAttrsFragment = `
  fragment organizationAttrs on Organization {
    id
    __typename
    createdAt
    updatedAt
    name
    photoURL
    domain
    featureFlags
    membersCanInvite
    membersCanAutoJoin
    authDomains
    authMethod
    authProvider {
      name
    }
    product
    templatesChannelId
    inviteToken
    requiredRoleForPublicSharing
    requestedManualBackupAt
    requestedManualBackupBy
    automatedBackupSchedule
    permissions {
      ...organizationPermissionsAttrs
    }
  }
  ${organizationPermissionsAttrsFragment}
`

export const organizationResourcesUsageFragment = `
  fragment organizationResourcesUsage on Organization {
    resourcesUsage {
      __typename
      id
      notes {
        limit
        current
      }
      storage {
        limit
        current
      }
    }
  }
`

const OrganizationsAPI = {
  fetchMyOrganizationContacts: gql`
    query myOrganizationContacts($name: String) {
      me {
        __typename
        id
        organization {
          id
          __typename
          contacts(byDisplayName: $name) {
            id
            __typename
            email
            displayName
            username
            photoURL
          }
        }
      }
    }
  `,

  fetchOrganizationFromDomain: graphQLBase(
    `query showPublicOrg($domain: String!) {
      showPublicOrganization(domain: $domain) {
        id
        __typename
        name
        photoURL
        domain
        authMethod
        authDomains
        membersCanAutoJoin
        product
        featureFlags
        authProvider {
          name
        }
      }
    }`,
    ({ showPublicOrganization }) => showPublicOrganization
  ),

  getResourcesUsageStatus: graphQLBase(
    `query getOrganizationResourcesUsage($organizationId: ID!) {
      showOrganization(id: $organizationId) {
        __typename
        resourcesUsage {
          notes {
            limit
            current
          }
          storage {
            limit
            current
          }
        }
      }
    }
  `,
    ({ showOrganization }) => showOrganization
  ),

  getOrganizationNotesCount: graphQLBase(
    `query getOrganizationNotesCount($organizationId: ID!) {
      showOrganization(id: $organizationId) {
        __typename
        notesCount
      }
    }`,
    ({ showOrganization }) => showOrganization.notesCount
  ),

  fetchOrganization({ apiEndpoint, apiToken }, organizationId) {
    return fetch(`${apiEndpoint}/organizations/${organizationId}`, {
      ...fetchBase({ apiToken }),
    }).then(basicResponseHandler)
  },

  fetchOrganizationPublicInfo({ apiEndpoint }, organizationId) {
    return fetch(`${apiEndpoint}/organizations/${organizationId}/publicInfo`, {
      ...fetchBase(),
    }).then(basicResponseHandler)
  },

  fetchOrganizationPublicInfoWithDomain({ apiEndpoint }, organizationDomain) {
    return fetch(`${apiEndpoint}/organizations/domains/${organizationDomain}`, {
      ...fetchBase(),
    })
      .then(rejectResponseAbove(400))
      .then(extractJSON)
      .then(throwOnDataErrors)
  },

  showJoinedOrganizations: gql`
    query showJoinedOrganizations {
      showJoinedOrganizations {
        id
        name
        photoURL
        domain
      }
    }
  `,

  updateOrganization: gql`
    mutation UpdateCurrentOrganization($organization: OrganizationUpdateInput) {
      updateCurrentOrganization(organization: $organization) {
        ...organizationAttrs
      }
    }
    ${organizationAttrsFragment}
  `,

  updateOrganizationDomain: gql`
    mutation UpdateOrganizationDomain($domain: String!) {
      updateOrganizationDomain(domain: $domain) {
        success
      }
    }
  `,

  setOrganizationAuthProvider: gql`
    mutation SetOrganizationAuthProvider(
      $input: SetOrganizationAuthProviderInput!
    ) {
      setOrganizationAuthProvider(input: $input) {
        success
      }
    }
  `,

  generateInviteToken: gql`
    mutation GenerateInviteToken {
      generateInviteToken
    }
  `,

  removeInviteToken: gql`
    mutation RemoveInviteToken {
      removeInviteToken {
        ...organizationAttrs
      }
    }
    ${organizationAttrsFragment}
  `,

  requestManualOrganizationBackup: gql`
    mutation requestManualOrganizationBackup {
      requestManualOrganizationBackup {
        ...organizationAttrs
      }
    }
    ${organizationAttrsFragment}
  `,

  uploadOrganizationPhotoURL({ apiEndpoint, apiToken }, { photoFile }) {
    return FilesAPI.uploadFile(
      { apiEndpoint, apiToken },
      {
        file: photoFile,
        folder: 'avatars',
      }
    )
  },
}

export default OrganizationsAPI
